import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft, Save, Trash2, Building2, User, MapPin, Wallet, Tag as TagIcon,
    Copy, Plus, Trash, CheckCircle2, XCircle, Info,
} from 'lucide-react';
import { api } from '../../lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const GST_STATES = [
    ['01', 'Jammu & Kashmir'], ['02', 'Himachal Pradesh'], ['03', 'Punjab'], ['04', 'Chandigarh'],
    ['05', 'Uttarakhand'], ['06', 'Haryana'], ['07', 'Delhi'], ['08', 'Rajasthan'],
    ['09', 'Uttar Pradesh'], ['10', 'Bihar'], ['11', 'Sikkim'], ['12', 'Arunachal Pradesh'],
    ['13', 'Nagaland'], ['14', 'Manipur'], ['15', 'Mizoram'], ['16', 'Tripura'],
    ['17', 'Meghalaya'], ['18', 'Assam'], ['19', 'West Bengal'], ['20', 'Jharkhand'],
    ['21', 'Odisha'], ['22', 'Chhattisgarh'], ['23', 'Madhya Pradesh'], ['24', 'Gujarat'],
    ['26', 'Dadra & Nagar Haveli'], ['27', 'Maharashtra'], ['28', 'Andhra Pradesh (Old)'],
    ['29', 'Karnataka'], ['30', 'Goa'], ['31', 'Lakshadweep'], ['32', 'Kerala'],
    ['33', 'Tamil Nadu'], ['34', 'Puducherry'], ['35', 'Andaman & Nicobar'],
    ['36', 'Telangana'], ['37', 'Andhra Pradesh'], ['38', 'Ladakh'],
];

const BUSINESS_TYPES = [
    { v: 'retail', l: 'Retail' },
    { v: 'wholesale', l: 'Wholesale / Distributor' },
    { v: 'manufacturer', l: 'Manufacturer' },
    { v: 'service', l: 'Service Provider' },
    { v: 'export', l: 'Export' },
    { v: 'ecommerce', l: 'E-commerce' },
    { v: 'b2c', l: 'B2C / End Consumer' },
    { v: 'other', l: 'Other' },
];

const TAX_TREATMENTS = [
    { v: 'regular', l: 'Regular (registered)' },
    { v: 'composition', l: 'Composition Scheme' },
    { v: 'unregistered', l: 'Unregistered' },
    { v: 'sez', l: 'SEZ (Special Economic Zone)' },
    { v: 'export', l: 'Overseas / Export' },
    { v: 'consumer', l: 'B2C Consumer' },
];

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

function todayIso() { return new Date().toISOString().slice(0, 10); }

function emptyForm() {
    return {
        name: '',
        display_name: '',
        gstin: '',
        pan: '',
        email: '',
        phone: '',
        alt_phone: '',
        website: '',
        payment_terms_days: 30,
        credit_limit: null,
        credit_days: null,
        currency: 'INR',
        business_type: '',
        tax_treatment: 'regular',
        tds_applicable: false,
        tds_percent: null,
        tds_section: '',
        billing_address: { line1: '', line2: '', city: '', state: '', state_code: '', pincode: '', country: 'India' },
        shipping_address: null,
        primary_contact: { name: '', designation: '', email: '', phone: '', is_primary: true },
        additional_contacts: [],
        tags: [],
        customer_since: todayIso(),
        reference_source: '',
        territory: '',
        default_discount_percent: null,
        opening_balance: 0,
        opening_balance_type: 'receivable',
        opening_balance_as_of: null,
        notes: '',
    };
}

export default function CustomerEditPage({ isNew }) {
    const nav = useNavigate();
    const { id } = useParams();
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState('basic');
    const [shipSameAsBill, setShipSameAsBill] = useState(true);
    const [tagInput, setTagInput] = useState('');
    const [form, setForm] = useState(emptyForm);

    useEffect(() => {
        if (!isNew && id) {
            api.invGetCustomer(id).then((c) => {
                setForm({
                    ...emptyForm(),
                    ...c,
                    shipping_address: c.shipping_address || null,
                    primary_contact: c.primary_contact || emptyForm().primary_contact,
                    additional_contacts: c.additional_contacts || [],
                    tags: c.tags || [],
                });
                setShipSameAsBill(!c.shipping_address);
            }).catch(() => toast.error('Failed to load customer'));
        }
    }, [id, isNew]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const setAddr = (bucket, k, v) => setForm((f) => ({ ...f, [bucket]: { ...(f[bucket] || emptyForm()[bucket]), [k]: v } }));
    const setContact = (k, v) => setForm((f) => ({ ...f, primary_contact: { ...(f.primary_contact || {}), [k]: v } }));

    const gstinValid = form.gstin ? GSTIN_REGEX.test(form.gstin) : null;
    const panValid = form.pan ? PAN_REGEX.test(form.pan) : null;

    // Auto-derive PAN + state from GSTIN
    useEffect(() => {
        if (form.gstin && gstinValid) {
            const sc = form.gstin.slice(0, 2);
            const pan = form.gstin.slice(2, 12);
            const stateName = GST_STATES.find(([c]) => c === sc)?.[1] || '';
            // Use functional setForm so we don't need `form` in deps and avoid
            // stale-closure races when GSTIN changes rapidly.
            setForm((f) => ({
                ...f,
                pan: f.pan || pan,
                billing_address: {
                    ...f.billing_address,
                    state_code: f.billing_address.state_code || sc,
                    state: f.billing_address.state || stateName,
                },
            }));
        }
    }, [form.gstin, gstinValid]);

    const onSave = async () => {
        if (!form.name.trim()) { toast.error('Customer name is required'); setTab('basic'); return; }
        if (form.gstin && !gstinValid) { toast.error('GSTIN format is invalid'); setTab('basic'); return; }
        if (form.pan && !panValid) { toast.error('PAN format is invalid'); setTab('basic'); return; }
        setSaving(true);
        try {
            const payload = { ...form };
            if (shipSameAsBill) payload.shipping_address = null;
            // Clean primary contact if empty
            if (!payload.primary_contact?.name) payload.primary_contact = null;
            const c = isNew ? await api.invCreateCustomer(payload) : await api.invUpdateCustomer(id, payload);
            toast.success(isNew ? 'Customer created' : 'Customer updated');
            nav(`/invoicing/customers/${c.id || id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const onDelete = async () => {
        if (!confirm('Delete this customer? (Customers with invoices will be soft-deactivated.)')) return;
        try {
            await api.invDeleteCustomer(id);
            toast.success('Customer removed');
            nav('/invoicing/customers');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Delete failed');
        }
    };

    const addTag = () => {
        const t = tagInput.trim();
        if (!t) return;
        if (form.tags.includes(t)) { setTagInput(''); return; }
        set('tags', [...form.tags, t]);
        setTagInput('');
    };
    const removeTag = (t) => set('tags', form.tags.filter((x) => x !== t));

    const addContact = () => set('additional_contacts', [...form.additional_contacts, { name: '', designation: '', email: '', phone: '' }]);
    const setAdditionalContact = (i, k, v) => set('additional_contacts', form.additional_contacts.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
    const removeAdditionalContact = (i) => set('additional_contacts', form.additional_contacts.filter((_, idx) => idx !== i));

    return (
        <div className="space-y-4 max-w-5xl" data-testid="customer-edit-page">
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="btn-back"><ArrowLeft size={16} /></Button>
                    <div>
                        <h1 className="text-2xl font-heading font-semibold tracking-tight">{isNew ? 'New Customer' : (form.name || 'Edit Customer')}</h1>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            {isNew ? 'Add a customer to start invoicing them' : `Editing existing customer profile`}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => nav(-1)} data-testid="btn-cancel">Cancel</Button>
                    <Button onClick={onSave} disabled={saving} data-testid="btn-save"><Save size={16} className="mr-1.5" /> {saving ? 'Saving…' : (isNew ? 'Create customer' : 'Save changes')}</Button>
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="w-full justify-start overflow-x-auto">
                    <TabsTrigger value="basic" data-testid="tab-basic"><Building2 size={14} className="mr-1.5" /> Basic</TabsTrigger>
                    <TabsTrigger value="address" data-testid="tab-address"><MapPin size={14} className="mr-1.5" /> Address</TabsTrigger>
                    <TabsTrigger value="contact" data-testid="tab-contact"><User size={14} className="mr-1.5" /> Contacts</TabsTrigger>
                    <TabsTrigger value="financial" data-testid="tab-financial"><Wallet size={14} className="mr-1.5" /> Financial</TabsTrigger>
                    <TabsTrigger value="advanced" data-testid="tab-advanced"><TagIcon size={14} className="mr-1.5" /> Tags & Notes</TabsTrigger>
                </TabsList>

                {/* BASIC */}
                <TabsContent value="basic" className="space-y-4 mt-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Business Identity</CardTitle>
                            <CardDescription>Core details identifying this customer's business.</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Field label="Business Name *" hint="Legal business name as per GST">
                                    <Input data-testid="input-name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Acme Traders Pvt Ltd" />
                                </Field>
                                <Field label="Display Name" hint="How to show on invoice (defaults to Business Name)">
                                    <Input data-testid="input-display-name" value={form.display_name} onChange={(e) => set('display_name', e.target.value)} placeholder="Optional" />
                                </Field>
                                <Field label="GSTIN" hint="15-char GST number — auto-fills PAN + state">
                                    <div className="relative">
                                        <Input data-testid="input-gstin" value={form.gstin} onChange={(e) => set('gstin', e.target.value.toUpperCase())} placeholder="27ABCDE1234F1Z5" className="font-mono pr-10" maxLength={15} />
                                        {form.gstin && (
                                            <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                                {gstinValid ? <CheckCircle2 size={16} className="text-emerald-600" /> : <XCircle size={16} className="text-red-500" />}
                                            </div>
                                        )}
                                    </div>
                                    {form.gstin && !gstinValid && <div className="text-[11px] text-red-600 mt-1">Invalid GSTIN format</div>}
                                    {gstinValid && <div className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1"><Info size={11} /> State {form.gstin.slice(0, 2)} · PAN {form.gstin.slice(2, 12)}</div>}
                                </Field>
                                <Field label="PAN" hint="Auto-derived from GSTIN if valid">
                                    <div className="relative">
                                        <Input data-testid="input-pan" value={form.pan} onChange={(e) => set('pan', e.target.value.toUpperCase())} placeholder="AAACS1234A" className="font-mono pr-10" maxLength={10} />
                                        {form.pan && (
                                            <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                                {panValid ? <CheckCircle2 size={16} className="text-emerald-600" /> : <XCircle size={16} className="text-red-500" />}
                                            </div>
                                        )}
                                    </div>
                                </Field>
                                <Field label="Business Type">
                                    <Select value={form.business_type} onValueChange={(v) => set('business_type', v)}>
                                        <SelectTrigger data-testid="select-business-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                                        <SelectContent>
                                            {BUSINESS_TYPES.map((b) => <SelectItem key={b.v} value={b.v}>{b.l}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </Field>
                                <Field label="Tax Treatment" hint="Determines how GST is applied">
                                    <Select value={form.tax_treatment} onValueChange={(v) => set('tax_treatment', v)}>
                                        <SelectTrigger data-testid="select-tax-treatment"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {TAX_TREATMENTS.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </Field>
                                <Field label="Customer Since">
                                    <Input data-testid="input-customer-since" type="date" value={form.customer_since || ''} onChange={(e) => set('customer_since', e.target.value)} />
                                </Field>
                                <Field label="Currency">
                                    <Select value={form.currency} onValueChange={(v) => set('currency', v)}>
                                        <SelectTrigger data-testid="select-currency"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="INR">INR — Indian Rupee</SelectItem>
                                            <SelectItem value="USD">USD — US Dollar</SelectItem>
                                            <SelectItem value="EUR">EUR — Euro</SelectItem>
                                            <SelectItem value="GBP">GBP — British Pound</SelectItem>
                                            <SelectItem value="AED">AED — UAE Dirham</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </Field>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Communication</CardTitle>
                            <CardDescription>How your team reaches this customer.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Field label="Email">
                                    <Input data-testid="input-email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="billing@acme.in" />
                                </Field>
                                <Field label="Website">
                                    <Input data-testid="input-website" value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="https://acme.in" />
                                </Field>
                                <Field label="Phone (Primary)">
                                    <Input data-testid="input-phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+91 98765 43210" />
                                </Field>
                                <Field label="Alternate Phone">
                                    <Input data-testid="input-alt-phone" value={form.alt_phone} onChange={(e) => set('alt_phone', e.target.value)} placeholder="Optional" />
                                </Field>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ADDRESS */}
                <TabsContent value="address" className="space-y-4 mt-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Billing Address</CardTitle>
                            <CardDescription>Registered address of the customer (used on invoice PDF).</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Field label="Line 1"><Input data-testid="input-addr1" value={form.billing_address.line1} onChange={(e) => setAddr('billing_address', 'line1', e.target.value)} placeholder="Building, street" /></Field>
                                <Field label="Line 2"><Input data-testid="input-addr2" value={form.billing_address.line2} onChange={(e) => setAddr('billing_address', 'line2', e.target.value)} placeholder="Landmark / area" /></Field>
                                <Field label="City"><Input data-testid="input-city" value={form.billing_address.city} onChange={(e) => setAddr('billing_address', 'city', e.target.value)} /></Field>
                                <Field label="State">
                                    <Select value={form.billing_address.state_code} onValueChange={(v) => {
                                        const s = GST_STATES.find(([c]) => c === v);
                                        setAddr('billing_address', 'state_code', v);
                                        setAddr('billing_address', 'state', s ? s[1] : '');
                                    }}>
                                        <SelectTrigger data-testid="select-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                                        <SelectContent className="max-h-64">
                                            {GST_STATES.map(([c, n]) => <SelectItem key={c} value={c}>{c} — {n}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </Field>
                                <Field label="Pincode"><Input data-testid="input-pincode" value={form.billing_address.pincode} onChange={(e) => setAddr('billing_address', 'pincode', e.target.value)} maxLength={6} /></Field>
                                <Field label="Country"><Input data-testid="input-country" value={form.billing_address.country} onChange={(e) => setAddr('billing_address', 'country', e.target.value)} /></Field>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <div>
                                <CardTitle className="text-base">Shipping Address</CardTitle>
                                <CardDescription>Where goods/services are delivered.</CardDescription>
                            </div>
                            <div className="flex items-center gap-2">
                                <Label className="text-xs text-muted-foreground">Same as billing</Label>
                                <Switch checked={shipSameAsBill} onCheckedChange={(v) => {
                                    setShipSameAsBill(v);
                                    if (v) set('shipping_address', null);
                                    else set('shipping_address', { ...form.billing_address });
                                }} data-testid="switch-same-address" />
                            </div>
                        </CardHeader>
                        {!shipSameAsBill && (
                            <CardContent>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Field label="Line 1"><Input value={form.shipping_address?.line1 || ''} onChange={(e) => setAddr('shipping_address', 'line1', e.target.value)} /></Field>
                                    <Field label="Line 2"><Input value={form.shipping_address?.line2 || ''} onChange={(e) => setAddr('shipping_address', 'line2', e.target.value)} /></Field>
                                    <Field label="City"><Input value={form.shipping_address?.city || ''} onChange={(e) => setAddr('shipping_address', 'city', e.target.value)} /></Field>
                                    <Field label="State">
                                        <Select value={form.shipping_address?.state_code || ''} onValueChange={(v) => {
                                            const s = GST_STATES.find(([c]) => c === v);
                                            setAddr('shipping_address', 'state_code', v);
                                            setAddr('shipping_address', 'state', s ? s[1] : '');
                                        }}>
                                            <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                                            <SelectContent className="max-h-64">
                                                {GST_STATES.map(([c, n]) => <SelectItem key={c} value={c}>{c} — {n}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Pincode"><Input value={form.shipping_address?.pincode || ''} onChange={(e) => setAddr('shipping_address', 'pincode', e.target.value)} /></Field>
                                    <Field label="Country"><Input value={form.shipping_address?.country || 'India'} onChange={(e) => setAddr('shipping_address', 'country', e.target.value)} /></Field>
                                </div>
                            </CardContent>
                        )}
                    </Card>
                </TabsContent>

                {/* CONTACTS */}
                <TabsContent value="contact" className="space-y-4 mt-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Primary Contact Person</CardTitle>
                            <CardDescription>The main person you'll interact with.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Field label="Name"><Input data-testid="input-contact-name" value={form.primary_contact?.name || ''} onChange={(e) => setContact('name', e.target.value)} placeholder="e.g. Rajesh Kumar" /></Field>
                                <Field label="Designation"><Input data-testid="input-contact-designation" value={form.primary_contact?.designation || ''} onChange={(e) => setContact('designation', e.target.value)} placeholder="e.g. Accounts Manager" /></Field>
                                <Field label="Email"><Input data-testid="input-contact-email" type="email" value={form.primary_contact?.email || ''} onChange={(e) => setContact('email', e.target.value)} /></Field>
                                <Field label="Phone"><Input data-testid="input-contact-phone" value={form.primary_contact?.phone || ''} onChange={(e) => setContact('phone', e.target.value)} /></Field>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <div>
                                <CardTitle className="text-base">Additional Contacts</CardTitle>
                                <CardDescription>Other stakeholders — dispatch, sales, ops etc.</CardDescription>
                            </div>
                            <Button variant="outline" size="sm" onClick={addContact} data-testid="btn-add-contact"><Plus size={14} className="mr-1" /> Add contact</Button>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            {form.additional_contacts.length === 0 && (
                                <div className="text-center py-4 text-xs text-muted-foreground">No additional contacts. Click "Add contact" to add one.</div>
                            )}
                            {form.additional_contacts.map((c, i) => (
                                <div key={i} data-testid={`extra-contact-${i}`} className="grid grid-cols-12 gap-2 items-start border-b border-border/40 pb-3 last:border-0">
                                    <div className="col-span-12 md:col-span-3"><Label className="text-[10px] uppercase text-muted-foreground">Name</Label><Input value={c.name} onChange={(e) => setAdditionalContact(i, 'name', e.target.value)} /></div>
                                    <div className="col-span-12 md:col-span-3"><Label className="text-[10px] uppercase text-muted-foreground">Designation</Label><Input value={c.designation} onChange={(e) => setAdditionalContact(i, 'designation', e.target.value)} /></div>
                                    <div className="col-span-6 md:col-span-3"><Label className="text-[10px] uppercase text-muted-foreground">Email</Label><Input type="email" value={c.email} onChange={(e) => setAdditionalContact(i, 'email', e.target.value)} /></div>
                                    <div className="col-span-6 md:col-span-2"><Label className="text-[10px] uppercase text-muted-foreground">Phone</Label><Input value={c.phone} onChange={(e) => setAdditionalContact(i, 'phone', e.target.value)} /></div>
                                    <div className="col-span-12 md:col-span-1 pt-4"><Button variant="ghost" size="icon" onClick={() => removeAdditionalContact(i)} className="text-red-600"><Trash size={14} /></Button></div>
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* FINANCIAL */}
                <TabsContent value="financial" className="space-y-4 mt-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Payment & Credit Terms</CardTitle>
                            <CardDescription>How and when the customer pays you.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <Field label="Payment terms (days)" hint="Net 30 = due 30 days after invoice">
                                    <Input data-testid="input-payment-terms" type="number" min={0} max={365} value={form.payment_terms_days} onChange={(e) => set('payment_terms_days', Number(e.target.value) || 0)} />
                                </Field>
                                <Field label="Credit limit (₹)" hint="Max outstanding you'll allow">
                                    <Input data-testid="input-credit-limit" type="number" min={0} step={100} value={form.credit_limit ?? ''} onChange={(e) => set('credit_limit', e.target.value ? Number(e.target.value) : null)} placeholder="No limit" />
                                </Field>
                                <Field label="Default discount %">
                                    <Input data-testid="input-default-discount" type="number" min={0} max={100} step={0.5} value={form.default_discount_percent ?? ''} onChange={(e) => set('default_discount_percent', e.target.value ? Number(e.target.value) : null)} placeholder="0" />
                                </Field>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Opening Balance</CardTitle>
                            <CardDescription>Any pre-existing balance carried over (skip if starting fresh).</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <Field label="Amount (₹)">
                                    <Input data-testid="input-opening-balance" type="number" step={0.01} value={form.opening_balance || 0} onChange={(e) => set('opening_balance', Number(e.target.value) || 0)} />
                                </Field>
                                <Field label="Type">
                                    <Select value={form.opening_balance_type} onValueChange={(v) => set('opening_balance_type', v)}>
                                        <SelectTrigger data-testid="select-opening-type"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="receivable">Receivable (they owe you)</SelectItem>
                                            <SelectItem value="payable">Payable (you owe them)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </Field>
                                <Field label="As of">
                                    <Input data-testid="input-opening-as-of" type="date" value={form.opening_balance_as_of || ''} onChange={(e) => set('opening_balance_as_of', e.target.value)} />
                                </Field>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">TDS (Tax Deducted at Source)</CardTitle>
                            <CardDescription>If this customer deducts TDS on your invoices.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center gap-3 mb-4">
                                <Switch checked={form.tds_applicable} onCheckedChange={(v) => set('tds_applicable', v)} data-testid="switch-tds" />
                                <Label className="text-sm">TDS applicable</Label>
                            </div>
                            {form.tds_applicable && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Field label="TDS %">
                                        <Input data-testid="input-tds-percent" type="number" min={0} max={30} step={0.5} value={form.tds_percent ?? ''} onChange={(e) => set('tds_percent', e.target.value ? Number(e.target.value) : null)} placeholder="e.g. 10" />
                                    </Field>
                                    <Field label="Section">
                                        <Input data-testid="input-tds-section" value={form.tds_section || ''} onChange={(e) => set('tds_section', e.target.value)} placeholder="e.g. 194C, 194J" />
                                    </Field>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ADVANCED */}
                <TabsContent value="advanced" className="space-y-4 mt-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Tags</CardTitle>
                            <CardDescription>Categorize for filtering (e.g. "premium", "north-region", "quarterly").</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex gap-2 mb-3">
                                <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} placeholder="Type a tag and press Enter" data-testid="input-tag" />
                                <Button variant="outline" size="sm" onClick={addTag}>Add</Button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {form.tags.length === 0 && <span className="text-xs text-muted-foreground">No tags yet</span>}
                                {form.tags.map((t) => (
                                    <Badge key={t} variant="secondary" data-testid={`tag-${t}`} className="cursor-pointer" onClick={() => removeTag(t)}>
                                        {t} <Trash size={10} className="ml-1.5" />
                                    </Badge>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Attribution & Territory</CardTitle>
                            <CardDescription>Track how customers came to you and where they're located.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Field label="Reference source" hint="How did they find you?">
                                    <Input data-testid="input-reference-source" value={form.reference_source || ''} onChange={(e) => set('reference_source', e.target.value)} placeholder="e.g. Referral, LinkedIn, Direct" />
                                </Field>
                                <Field label="Territory / Region">
                                    <Input data-testid="input-territory" value={form.territory || ''} onChange={(e) => set('territory', e.target.value)} placeholder="e.g. West, South, Mumbai" />
                                </Field>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Internal Notes</CardTitle>
                            <CardDescription>Private notes visible only to you. Not shown on invoices.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Textarea data-testid="input-notes" value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={4} placeholder="e.g. Prefers WhatsApp communication. Founder is Sanjay. Always pays on 25th…" />
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            <div className="flex items-center justify-between gap-2 pb-8 pt-2 border-t border-border/50">
                {!isNew ? (
                    <Button variant="outline" onClick={onDelete} className="text-red-600 hover:text-red-700" data-testid="btn-delete">
                        <Trash2 size={16} className="mr-1.5" /> Delete customer
                    </Button>
                ) : <div />}
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => nav(-1)}>Cancel</Button>
                    <Button onClick={onSave} disabled={saving} data-testid="btn-save-bottom">
                        <Save size={16} className="mr-1.5" /> {saving ? 'Saving…' : (isNew ? 'Create customer' : 'Save changes')}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">{label}</Label>
            {children}
            {hint && <div className="text-[10px] text-muted-foreground/80">{hint}</div>}
        </div>
    );
}
